import React from 'react';
import { T } from '../constants';

/**
 * PrefilterState
 *
 * Renders the "Pre-filter state" checklist on the decision detail page.
 * Consumes the `pre_filter` block from the detail API response. Each gate
 * has a name (human readable), a desc (Pine-level technical description),
 * a status (currently always "pass" for landed decisions — see footnote),
 * and an optional evidence string (e.g. `trigger = Cobra15S`).
 *
 * Returns null if the block is missing or has zero gates, so callers can
 * unconditionally render `<PrefilterState block={data.pre_filter} />`
 * without guarding — strategies with no pre-filter produce no section.
 */
export default function PrefilterState({ block }) {
  if (!block || !Array.isArray(block.gates) || block.gates.length === 0) {
    return null;
  }

  const { gates, gate_count, footnote } = block;
  const passCount = gates.filter(g => g.status === 'pass').length;
  const allPassed = passCount === gate_count;

  return (
    <section style={styles.section}>
      <div style={styles.header}>
        <h3 style={styles.h}>
          Pre-filter state
          <span style={styles.hint}>
            · Stage-0 gates required for this source
          </span>
        </h3>
        <div style={styles.headerRight}>
          <span
            style={{
              ...styles.countPill,
              color:       allPassed ? T.bull       : T.bear,
              background:  allPassed ? T.bullBg     : T.bearBg,
              borderColor: allPassed ? T.bullBorder : T.bearBorder,
            }}
          >
            {passCount} of {gate_count} pass
          </span>
        </div>
      </div>

      <div style={styles.list}>
        {gates.map((g, i) => (
          <GateRow key={g.key || i} gate={g} isLast={i === gates.length - 1} />
        ))}
      </div>

      {footnote && (
        <div style={styles.footnote}>
          <span style={styles.footnoteIcon} aria-hidden="true">i</span>
          <span>{footnote}</span>
        </div>
      )}
    </section>
  );
}


function GateRow({ gate, isLast }) {
  const cfg = STATUS_CFG[gate.status] || STATUS_CFG.pass;

  return (
    <div
      style={{
        ...styles.row,
        borderBottom: isLast
          ? 'none'
          : `1px solid var(--tv-border-lt, rgba(51,68,85,0.35))`,
      }}
    >
      <div
        style={{
          ...styles.iconCell,
          background: cfg.iconBg,
          color:      cfg.color,
          borderColor: cfg.border,
        }}
        aria-label={cfg.label}
      >
        {cfg.symbol}
      </div>

      <div style={styles.bodyCell}>
        <div style={styles.gateName}>{gate.name}</div>
        <div style={styles.gateDesc}>{gate.desc}</div>
        {gate.evidence && (
          <div style={styles.gateEvidence}>
            <span style={styles.evidenceLabel}>evidence</span>
            <span style={styles.evidenceValue}>{gate.evidence}</span>
          </div>
        )}
      </div>

      <div style={styles.pillCell}>
        <span
          style={{
            ...styles.pill,
            color:       cfg.color,
            background:  cfg.pillBg,
            borderColor: cfg.border,
          }}
        >
          {cfg.label}
        </span>
      </div>
    </div>
  );
}


// ─── Status configuration ─────────────────────────────────────────────────

const STATUS_CFG = {
  pass: {
    label:  'PASS',
    symbol: <CheckIcon />,
    color:       T.bull,
    pillBg:      T.bullBg,
    iconBg:      'rgba(38,166,154,0.10)',
    border:      T.bullBorder,
  },
  fail: {
    label:  'FAIL',
    symbol: <XIcon />,
    color:       T.bear,
    pillBg:      T.bearBg,
    iconBg:      'rgba(239,83,80,0.10)',
    border:      T.bearBorder,
  },
  skip: {
    label:  'SKIP',
    symbol: <DashIcon />,
    color:       T.textMuted,
    pillBg:      'rgba(79,89,102,0.08)',
    iconBg:      'rgba(79,89,102,0.06)',
    border:      'rgba(79,89,102,0.25)',
  },
  info: {
    label:  'INFO',
    symbol: <InfoIcon />,
    color:       T.blue,
    pillBg:      T.blueBg,
    iconBg:      'rgba(30,136,229,0.08)',
    border:      T.blueBorder,
  },
};


// ─── Icons (inline SVG, 12px; match the project's existing aesthetic) ────

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M13.5 4 L6 11.5 L2.5 8" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" />
    </svg>
  );
}

function DashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 8 L12 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 7 L8 11.5 M8 4.5 L8 4.5" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" />
    </svg>
  );
}


// ─── Styles (match existing DecisionDetail.jsx conventions) ──────────────

const styles = {
  section: {
    background: 'var(--tv-surface)',
    border: '1px solid var(--tv-border)',
    borderRadius: 4,
    padding: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
  },
  h: {
    margin: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: T.textMuted,
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  hint: {
    fontWeight: 400,
    letterSpacing: '0.02em',
    textTransform: 'none',
    color: T.textDim,
    fontSize: 10,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  countPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 3,
    border: '1px solid',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    fontFamily: 'Roboto Mono, monospace',
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
  },

  row: {
    display: 'grid',
    gridTemplateColumns: '28px 1fr auto',
    gap: 14,
    alignItems: 'start',
    padding: '12px 0',
  },
  iconCell: {
    width: 22,
    height: 22,
    marginTop: 2,
    borderRadius: '50%',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bodyCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  gateName: {
    fontSize: 13,
    fontWeight: 600,
    color: T.text,
    lineHeight: 1.3,
  },
  gateDesc: {
    fontSize: 11,
    color: T.textDim,
    fontFamily: 'Roboto Mono, monospace',
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  gateEvidence: {
    marginTop: 4,
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '3px 8px',
    background: 'var(--tv-bg)',
    border: '1px solid var(--tv-border)',
    borderRadius: 3,
    alignSelf: 'flex-start',
  },
  evidenceLabel: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: T.textMuted,
  },
  evidenceValue: {
    fontSize: 11,
    color: T.text,
    fontFamily: 'Roboto Mono, monospace',
  },
  pillCell: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 4,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 3,
    border: '1px solid',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    fontFamily: 'Roboto Mono, monospace',
  },

  footnote: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: '1px solid var(--tv-border)',
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    fontSize: 11,
    color: T.textDim,
    lineHeight: 1.55,
  },
  footnoteIcon: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `1px solid ${T.textMuted}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 600,
    color: T.textMuted,
    fontFamily: 'Roboto Mono, monospace',
    flexShrink: 0,
    marginTop: 1,
  },
};
